import React, { useState } from 'react';
import { Save, Loader2, GraduationCap, Languages, BadgeCheck } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, Formacao, Idioma, Certificacao } from '../../shared/atlasApi';
import ListaEditavel, { inputCls, labelCls } from '../../shared/ListaEditavel';
import FeedbackBanner, { Feedback } from '../../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeEditar: boolean;
  onSalvo: () => void;
}

const NIVEIS_FORMACAO = [
  { v: 'medio', l: 'Ensino médio' },
  { v: 'tecnico', l: 'Técnico' },
  { v: 'superior', l: 'Superior' },
  { v: 'pos', l: 'Pós-graduação' },
  { v: 'mestrado', l: 'Mestrado' },
  { v: 'doutorado', l: 'Doutorado' },
];
const SITUACOES = [
  { v: 'concluido', l: 'Concluído' },
  { v: 'em_andamento', l: 'Em andamento' },
  { v: 'trancado', l: 'Trancado' },
];
const NIVEIS_IDIOMA = [
  { v: 'basico', l: 'Básico' },
  { v: 'intermediario', l: 'Intermediário' },
  { v: 'avancado', l: 'Avançado' },
  { v: 'fluente', l: 'Fluente' },
  { v: 'nativo', l: 'Nativo' },
];

const AbaFormacao: React.FC<Props> = ({ user, colaborador, podeEditar, onSalvo }) => {
  const e = colaborador.enriquecimento || {};
  const [formacao, setFormacao] = useState<Formacao[]>(
    Array.isArray(e.formacao) ? e.formacao.map(f => ({ ...f })) : [],
  );
  const [idiomas, setIdiomas] = useState<Idioma[]>(
    Array.isArray(e.idiomas) ? e.idiomas.map(i => ({ ...i })) : [],
  );
  const [certificacoes, setCertificacoes] = useState<Certificacao[]>(
    Array.isArray(e.certificacoes) ? e.certificacoes.map(c => ({ ...c })) : [],
  );
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const salvar = async () => {
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.putFormacao(user, colaborador.funccodi, formacao);
      await atlasApi.putIdiomas(user, colaborador.funccodi, idiomas);
      await atlasApi.putCertificacoes(user, colaborador.funccodi, certificacoes);
      setFeedback({ tipo: 'ok', msg: 'Formação salva com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-6">
      <FeedbackBanner feedback={feedback} />

      {/* Formação acadêmica */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <GraduationCap className="w-4 h-4" /> Formação acadêmica
        </div>
        <ListaEditavel<Formacao>
          itens={formacao}
          onChange={setFormacao}
          novoItem={() => ({ nivel: 'superior', curso: '', instituicao: '', ano_conclusao: '', situacao: 'concluido' })}
          rotuloAdd="adicionar formação"
          vazioMsg="Nenhuma formação cadastrada."
          podeEditar={podeEditar}
          renderLinha={(f, atualizar) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nível</label>
                <select className={inputCls} disabled={!podeEditar} value={f.nivel || 'superior'} onChange={ev => atualizar({ nivel: ev.target.value })}>
                  {NIVEIS_FORMACAO.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Curso</label>
                <input className={inputCls} disabled={!podeEditar} value={f.curso || ''} onChange={ev => atualizar({ curso: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Instituição</label>
                <input className={inputCls} disabled={!podeEditar} value={f.instituicao || ''} onChange={ev => atualizar({ instituicao: ev.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Ano conclusão</label>
                  <input className={inputCls} disabled={!podeEditar} value={f.ano_conclusao || ''} onChange={ev => atualizar({ ano_conclusao: ev.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Situação</label>
                  <select className={inputCls} disabled={!podeEditar} value={f.situacao || 'concluido'} onChange={ev => atualizar({ situacao: ev.target.value })}>
                    {SITUACOES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        />
      </section>

      {/* Idiomas */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <Languages className="w-4 h-4" /> Idiomas
        </div>
        <ListaEditavel<Idioma>
          itens={idiomas}
          onChange={setIdiomas}
          novoItem={() => ({ idioma: '', nivel: 'basico' })}
          rotuloAdd="adicionar idioma"
          vazioMsg="Nenhum idioma cadastrado."
          podeEditar={podeEditar}
          renderLinha={(i, atualizar) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Idioma</label>
                <input className={inputCls} disabled={!podeEditar} value={i.idioma || ''} onChange={ev => atualizar({ idioma: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Nível</label>
                <select className={inputCls} disabled={!podeEditar} value={i.nivel || 'basico'} onChange={ev => atualizar({ nivel: ev.target.value })}>
                  {NIVEIS_IDIOMA.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>
          )}
        />
      </section>

      {/* Certificações */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
          <BadgeCheck className="w-4 h-4" /> Certificações
        </div>
        <ListaEditavel<Certificacao>
          itens={certificacoes}
          onChange={setCertificacoes}
          novoItem={() => ({ nome: '', emissor: '', data_emissao: '', data_validade: '' })}
          rotuloAdd="adicionar certificação"
          vazioMsg="Nenhuma certificação cadastrada."
          podeEditar={podeEditar}
          renderLinha={(c, atualizar) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nome</label>
                <input className={inputCls} disabled={!podeEditar} value={c.nome || ''} onChange={ev => atualizar({ nome: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Emissor</label>
                <input className={inputCls} disabled={!podeEditar} value={c.emissor || ''} onChange={ev => atualizar({ emissor: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Data de emissão</label>
                <input type="date" className={inputCls} disabled={!podeEditar} value={(c.data_emissao || '').slice(0, 10)} onChange={ev => atualizar({ data_emissao: ev.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Data de validade</label>
                <input type="date" className={inputCls} disabled={!podeEditar} value={(c.data_validade || '').slice(0, 10)} onChange={ev => atualizar({ data_validade: ev.target.value })} />
              </div>
            </div>
          )}
        />
      </section>

      {podeEditar && (
        <div className="flex justify-end pt-1">
          <button
            onClick={salvar}
            disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar formação
          </button>
        </div>
      )}
    </div>
  );
};

export default AbaFormacao;
